import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Form, Input, Select, message, Tooltip, Switch, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import api from '../services/api';
import EditorTexto from '../components/EditorTexto';

const { Option } = Select;

const VARIAVEIS = [
  { label: 'Nome do cliente', valor: '{{cliente.nome}}' },
  { label: 'CNPJ do cliente', valor: '{{cliente.cnpj}}' },
  { label: 'Nº da proposta', valor: '{{proposta.numero}}' },
  { label: 'Valor da proposta', valor: '{{proposta.valor}}' },
  { label: 'Título da proposta', valor: '{{proposta.titulo}}' },
  { label: 'Validade da proposta', valor: '{{proposta.validade}}' },
  { label: 'Vencimento do boleto', valor: '{{boleto.vencimento}}' },
  { label: 'Valor do boleto', valor: '{{boleto.valor}}' },
  { label: 'Linha digitável', valor: '{{boleto.linha_digitavel}}' },
  { label: 'Nº da NFS-e', valor: '{{nfse.numero}}' },
  { label: 'Data emissão NFS-e', valor: '{{nfse.data_emissao}}' },
  { label: 'Valor da NFS-e', valor: '{{nfse.valor}}' },
  { label: 'Nome do usuário', valor: '{{usuario.nome}}' },
  { label: 'Data de hoje', valor: '{{data_hoje}}' },
];

const ANEXOS_OPCOES = [
  { label: 'Boleto PDF', value: 'boleto_pdf' },
  { label: 'NFS-e PDF', value: 'nfse_pdf' },
  { label: 'NFS-e XML', value: 'nfse_xml' },
  { label: 'Proposta PDF', value: 'proposta_pdf' },
];

function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [modalPreview, setModalPreview] = useState(false);
  const [templateAtual, setTemplateAtual] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [form] = Form.useForm();
  const editorRef = useRef(null);

  useEffect(() => { carregarTemplates(); }, []);

  const carregarTemplates = async () => {
    try {
      setLoading(true);
      const r = await api.get('/comunicacao/templates');
      setTemplates(r.data || []);
    } catch { message.error('Erro ao carregar templates'); }
    finally { setLoading(false); }
  };

  const abrirModal = (template = null) => {
    setTemplateAtual(template);
    if (template) {
      form.setFieldsValue({
        ...template,
        anexos_padrao: template.anexos_padrao || [],
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ remetente: 'financeiro', anexos_padrao: [] });
    }
    setModalAberto(true);
  };

  const salvar = async (values) => {
    try {
      if (templateAtual) {
        await api.put('/comunicacao/templates/' + templateAtual.id, values);
        message.success('Template atualizado!');
      } else {
        await api.post('/comunicacao/templates', values);
        message.success('Template criado!');
      }
      setModalAberto(false);
      carregarTemplates();
    } catch(e) { message.error('Erro: ' + (e.response?.data?.erro || e.message)); }
  };

  // a variavel entra onde o cursor estiver no editor
  const inserirVariavel = (variavel) => {
    if (editorRef.current) { editorRef.current.inserir(variavel); return; }
    const campo = form.getFieldValue('corpo') || '';
    form.setFieldsValue({ corpo: campo + variavel });
  };

  const verPreview = (template) => {
    const corpo = template.corpo || '';
    const html = `
      <div style="font-family:arial,sans-serif;max-width:600px;margin:0 auto">
        <div style="background:#098fdd;padding:20px;text-align:center">
          <img src="https://erp.celum.com.br/email/celum_horizontal_branco.png" width="150" alt="Celum">
        </div>
        <div style="background:#fff;border-top:5px solid #098fdd;border-bottom:5px solid #098fdd;padding:30px;font-size:14px;color:#333;line-height:21px">
          ${corpo}
        </div>
        <div style="background:#fff;padding:15px 25px;border-top:1px solid #eee;font-size:12px;color:#999;text-align:center">
          Celum Inspeções Técnicas e Comércio LTDA
        </div>
      </div>
    `;
    setPreviewHtml(html);
    setModalPreview(true);
  };

  const colunas = [
    { title: 'Nome', dataIndex: 'nome', ellipsis: true },
    { title: 'Descrição', dataIndex: 'descricao', ellipsis: true },
    { title: 'Remetente', dataIndex: 'remetente', width: 120, render: v => <Tag color={v === 'comercial' ? 'blue' : 'green'}>{v}</Tag> },
    { title: 'Tipo', dataIndex: 'fixo', width: 80, render: v => <Tag color={v ? 'orange' : 'default'}>{v ? 'Padrão' : 'Custom'}</Tag> },
    { title: 'Ações', width: 120, render: (_, r) => (
      <Space size={4}>
        <Tooltip title="Preview"><Button size="small" icon={<EyeOutlined />} onClick={() => verPreview(r)} /></Tooltip>
        <Tooltip title="Editar"><Button size="small" icon={<EditOutlined />} onClick={() => abrirModal(r)} /></Tooltip>
      </Space>
    )},
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Templates de E-mail</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => abrirModal()}>Novo Template</Button>
      </div>

      <Card size="small">
        <Table columns={colunas} dataSource={templates} rowKey="id" size="small" loading={loading}
          locale={{ emptyText: 'Nenhum template cadastrado.' }} />
      </Card>

      {/* Modal Editar/Criar */}
      <Modal title={templateAtual ? 'Editar Template' : 'Novo Template'} open={modalAberto}
        onCancel={() => setModalAberto(false)} footer={null} width={800} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={salvar}>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item name="nome" label="Nome do Template" style={{ flex: 2 }} rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="remetente" label="Remetente" style={{ flex: 1 }} rules={[{ required: true }]}>
              <Select>
                <Option value="financeiro">financeiro@celum.com.br</Option>
                <Option value="comercial">comercial@celum.com.br</Option>
              </Select>
            </Form.Item>
          </div>
          <Form.Item name="descricao" label="Descrição">
            <Input />
          </Form.Item>
          <Form.Item name="assunto" label="Assunto do E-mail" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="anexos_padrao" label="Anexos Padrão">
            <Select mode="multiple" placeholder="Selecione os anexos padrão">
              {ANEXOS_OPCOES.map(a => <Option key={a.value} value={a.value}>{a.label}</Option>)}
            </Select>
          </Form.Item>

          <Divider>Corpo do E-mail</Divider>

          {/* Colinha de variáveis */}
          <div style={{ marginBottom: 8, background: '#f6f6f6', padding: 8, borderRadius: 6 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>📋 Clique para inserir variável:</div>
            <Space wrap size={4}>
              {VARIAVEIS.map(v => (
                <Button key={v.valor} size="small" onClick={() => inserirVariavel(v.valor)}
                  style={{ fontSize: 11 }}>
                  {v.label}
                </Button>
              ))}
            </Space>
          </div>

          <Form.Item name="corpo" label="Corpo do e-mail" rules={[{ required: true, message: 'Escreva o texto do e-mail' }]}>
            <EditorTexto ref={editorRef} />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => verPreview({ corpo: form.getFieldValue('corpo') })}>
              <EyeOutlined /> Preview
            </Button>
            <Space>
              <Button onClick={() => setModalAberto(false)}>Cancelar</Button>
              <Button type="primary" htmlType="submit">Salvar</Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Modal Preview */}
      <Modal title="Preview do Template" open={modalPreview} onCancel={() => setModalPreview(false)}
        footer={null} width={700}>
        <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
      </Modal>
    </div>
  );
}

export default Templates;
